import type { AisleMetrics } from "../../api/rest";

/**
 * Which aisle the overview opens on (ADR 276).
 *
 * A dashboard tile that starts empty and waits for a pick is a mini-app, not an
 * overview — and until a per-user layout exists (ADR 273 stage 2) any pick is lost on
 * reload anyway. So the widget opens on the aisle that most needs a supervisor, and the
 * picker is how you leave that default, not how you reach a first answer.
 */

/** Below this, an aisle's disagreement with the WMS is worth a look. Same threshold the
 *  accuracy widget tones on, so two cards never disagree about what "good" is. */
export const AGREEMENT_WARN_PCT = 95;

/**
 * Worst first: least agreement wins, ties broken by fullness — a full aisle that
 * disagrees is harder to fix than an empty one that does.
 *
 * An aisle with **no** comparison sorts last regardless of how full it is. Leading with
 * one would present "we have not measured this" as the day's worst problem, and the whole
 * point of the default is that it names something real.
 */
export function byAttention(a: AisleMetrics, b: AisleMetrics): number {
  const rank = (x: AisleMetrics) => (x.divergence ? x.divergence.agreementPct : Number.POSITIVE_INFINITY);
  const d = rank(a) - rank(b);
  if (d !== 0) return d;
  const full = (x: AisleMetrics) => x.occupancy.occupancyPct ?? -1;
  return full(b) - full(a);
}

/** The aisle to open on, or undefined when there are none. */
export function leadAisle(aisles: AisleMetrics[]): AisleMetrics | undefined {
  return aisles.length ? [...aisles].sort(byAttention)[0] : undefined;
}

/** Whether an aisle should carry a visible warning marker in the picker. Absent data is
 *  never a warning: "not measured" is not "bad" (ADR 254). */
export function needsAttention(a: AisleMetrics): boolean {
  return a.divergence != null && a.divergence.agreementPct < AGREEMENT_WARN_PCT;
}
